import { useState, useEffect, useRef } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { aiProvider } from '@/lib/ai';
import { useStore } from '@/store/useStore';
import ProductCard from '@/components/ProductCard';
import type { Product, ProductRecommendation } from '@/types/product';

export default function AIDiscoveryHome() {
  const [prompt, setPrompt] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [recommendations, setRecommendations] = useState<ProductRecommendation[]>([]);
  const [summary, setSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [speechSupported, setSpeechSupported] = useState(false);

  const recognitionRef = useRef<any>(null);
  const synthesisRef = useRef<SpeechSynthesisUtterance | null>(null);

  const anonymousProfileId = useStore(state => state.anonymousProfileId);
  const setAnonymousProfileId = useStore(state => state.setAnonymousProfileId);

  useEffect(() => {
    // Check for speech recognition support
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    setSpeechSupported(!!SpeechRecognition && 'speechSynthesis' in window);

    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;

      recognitionRef.current.onresult = (event: any) => {
        const current = event.resultIndex;
        const transcriptText = event.results[current][0].transcript;
        setTranscript(transcriptText);

        if (event.results[current].isFinal) {
          setPrompt(transcriptText);
          setIsListening(false);
        }
      };

      recognitionRef.current.onerror = () => {
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    // Load all products
    loadProducts();

    // Initialize anonymous profile if needed
    if (!anonymousProfileId) {
      const newId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setAnonymousProfileId(newId);
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const loadProducts = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'products'));
      const allProducts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product));
      setProducts(allProducts);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      setTranscript('');
      setIsListening(true);
      recognitionRef.current.start();
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const speak = (text: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.9;
      utterance.pitch = 1;
      synthesisRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const searchPrompt = prompt.trim();
    if (!searchPrompt || !products.length) return;

    setLoading(true);

    try {
      // Get recommendations from AI
      const result = await aiProvider.generateRecommendations({
        prompt: searchPrompt,
        products,
      });

      setRecommendations(result.recommendations);
      setSummary(result.summary);

      // Speak a brief confirmation
      speak(`Found ${result.recommendations.length} products for you`);

      // Store prompt in anonymous profile (simplified for demo)
      // In production, this would be stored in Firestore
    } catch (error) {
      console.error('Error getting recommendations:', error);
      setSummary('Sorry, there was an error processing your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-forest-dark via-forest to-forest-light">
      {/* Main Input Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-serif font-bold text-warm mb-4">
            AI Gear Discovery
          </h1>
          <p className="text-xl text-warm-dark">
            Describe what you need, and we'll find the perfect gear for your adventure
          </p>
        </div>

        {/* Input Form */}
        <form onSubmit={handleSubmit} className="relative">
          <div className="bg-white rounded-2xl shadow-2xl p-8">
            <div className="flex gap-4 items-start mb-4">
              <textarea
                value={isListening ? transcript : prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="E.g., I need waterproof trail running shoes for muddy terrain, size 10, under £150..."
                className="flex-1 min-h-[120px] px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber focus:border-transparent resize-none text-slate"
                disabled={isListening}
              />

              {/* Voice Button */}
              {speechSupported && (
                <button
                  type="button"
                  onClick={isListening ? stopListening : startListening}
                  className={`flex-shrink-0 p-4 rounded-full transition-all ${
                    isListening
                      ? 'bg-red-500 animate-pulse'
                      : 'bg-amber hover:bg-amber-dark'
                  }`}
                  title={isListening ? 'Stop listening' : 'Start voice input'}
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                </button>
              )}
            </div>

            {isListening && (
              <div className="mb-4 p-3 bg-amber-light rounded-lg">
                <p className="text-sm text-amber-dark font-medium">
                  Listening... {transcript && `"${transcript}"`}
                </p>
              </div>
            )}

            {!speechSupported && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  💡 Voice input not supported in this browser. Using text-only mode.
                </p>
              </div>
            )}

            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading || !prompt.trim()}
                className="flex-1 px-8 py-4 bg-forest text-warm font-semibold rounded-lg hover:bg-forest-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-warm border-t-transparent rounded-full animate-spin" />
                    Finding gear...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    Find My Gear
                  </>
                )}
              </button>

              {(prompt || recommendations.length > 0) && (
                <button
                  type="button"
                  onClick={() => {
                    setPrompt('');
                    setRecommendations([]);
                    setSummary('');
                  }}
                  className="px-6 py-4 border-2 border-gray-300 text-slate font-semibold rounded-lg hover:border-gray-400 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </form>

        {/* Example Prompts */}
        {!recommendations.length && !loading && (
          <div className="mt-8 text-center">
            <p className="text-warm-dark mb-4 text-sm">Try these examples:</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {[
                'Waterproof hiking boots for winter, size 9, under £200',
                'Lightweight tent for 2 people, 3-season, packable',
                'Running vest with hydration for ultra marathons',
                'Insulated jacket for alpine climbing, mens large',
              ].map((example) => (
                <button
                  key={example}
                  onClick={() => setPrompt(example)}
                  className="px-4 py-2 bg-forest-light text-warm rounded-full text-sm hover:bg-forest transition-colors"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {(recommendations.length > 0 || summary) && (
        <div className="bg-warm py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {summary && (
              <div className="mb-8 text-center">
                <p className="text-lg text-gray-700">
                  <span className="font-semibold text-forest">Understanding:</span> {summary}
                </p>
              </div>
            )}

            {recommendations.length > 0 && (
              <>
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-3xl font-serif font-bold text-slate">
                    Recommended for You
                  </h2>
                  <p className="text-gray-600">
                    {recommendations.length} {recommendations.length === 1 ? 'product' : 'products'} found
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {recommendations.map(rec => (
                    <ProductCard
                      key={rec.product.id}
                      product={rec.product}
                      reason={rec.reason}
                      relevanceScore={rec.relevanceScore}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* How it Works */}
      {!recommendations.length && !loading && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-warm">
            <h3 className="text-2xl font-serif font-bold mb-6 text-center">How AI Discovery Works</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-amber rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🎤</span>
                </div>
                <h4 className="font-semibold mb-2">1. Describe Your Needs</h4>
                <p className="text-sm text-warm-dark">Use voice or text to tell us what you're looking for</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-amber rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🤖</span>
                </div>
                <h4 className="font-semibold mb-2">2. AI Analyzes</h4>
                <p className="text-sm text-warm-dark">Our AI understands your activity, conditions, and preferences</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-amber rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">🎯</span>
                </div>
                <h4 className="font-semibold mb-2">3. Get Personalized Results</h4>
                <p className="text-sm text-warm-dark">Receive ranked recommendations with explanations</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
